import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Avatar,
  Typography,
  Button,
  IconButton,
  Stack,
  useTheme,
  Tooltip,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { printHtml } from "../../utils/htmlToPrint";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  CalendarToday, FamilyRestroom, FormatSize as FormatSizeIcon,
  PictureAsPdf as PictureAsPdfIcon, Home, Phone, School
} from "@mui/icons-material";

const quickPalettes = [
  "#1e1e2f",
  "#0b6623",
  "#8a2be2",
  "#c68642",
  "#2b547e",
  "#b71c1c",
  "#004d40",
  "#0066cc",
];

export default function StudentIDCards() {
  const theme = useTheme();
  const location = useLocation();
  const { students } = location.state || [];

  const initialColors = useMemo(() => {
    const map = {};
    students.forEach((s, i) => {
      map[s.id] = quickPalettes[i % quickPalettes.length];
    });
    return map;
  }, [students]);

  const [colors, setColors] = useState(initialColors);
  const [globalColor, setGlobalColor] = useState("");
  // const [bottom, setBottom] = useState(initialColors);
  // const [top, setTop] = useState(initialColors);
  const [fontcolor, setFontcolor] = useState("white");
  // const [height, setHeight] = useState(205);
  const [layout, setLayout] = useState(true);
  // const [hide, setHide] = useState("");

  const applyGlobalColor = () => {
    if (!globalColor) return;
    const next = { ...colors };
    students.forEach((s) => (next[s.id] = globalColor));
    setColors(next);
  };

  const navigate = useNavigate();

  const handleBack = () => navigate(-1);

  const handleLayout = () => {
    setLayout(!layout);
  };

  const onPrint = () => {
    printHtml("student-id-cards-root");
    window.location.reload();
  };

  const handleDownloadPDF = async () => {
    const el = document.getElementById("student-id-cards-root");
    if (!el) return;

    try {
      // Add styles for PDF
      const style = document.createElement('style');
      style.textContent = `
      .two-line-text {
        display: -webkit-box !important;
        -webkit-line-clamp: 2 !important;
        -webkit-box-orient: vertical !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        word-break: break-word !important;
      }
    `;
      el.style.padding = '20px';
      el.appendChild(style);

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      // Remove the added style
      el.removeChild(style);

      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a5");
      const pdfWidth = 210;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(img, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("id-cards.pdf");
    } catch (err) {
      console.error("PDF error:", err);
    }
  };

  const InfoRow = ({ icon: Icon, label, value, children, color, ...props }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', ...props.sx }}>
      <Icon sx={{ fontSize: 14, color: color || theme.palette.primary.main }} />
      <Typography fontSize={11} variant="caption" lineHeight={1.3} {...props}>
        <strong>{label}:</strong> {value || children}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ px: 5 }}>
      {/* Controls */}
      <Card sx={{ mb: 2, p: 2, }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <ColorLensIcon sx={{ fontSize: 25 }} />
            <Stack direction="row" spacing={1}>
              {quickPalettes.map((c) => (
                <IconButton
                  key={c}
                  onClick={() => {
                    setGlobalColor(c);
                    const next = { ...colors };
                    students.forEach((s) => (next[s.id] = c));
                    setColors(next);
                  }}
                  sx={{ bgcolor: c, width: 20, height: 20, border: "1px solid rgba(0,0,0,0.15)" }}
                />
              ))}
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1">Global Color:</Typography>
            <input
              type="color"
              value={globalColor || "#000000"}
              onChange={(e) => setGlobalColor(e.target.value)}
              style={{ width: 42, height: 32, border: "none", background: "transparent" }}
            />
            <Button variant="contained" size="small" color="warning" onClick={applyGlobalColor}>
              Apply
            </Button>

            <Button
              size="small"
              variant={layout ? "contained" : "outlined"}
              startIcon={<FormatSizeIcon />}
              onClick={handleLayout}
              sx={{ fontSize: "0.75rem", textTransform: "none" }}
            >
              {layout ? "Portrait" : "Landscape"}
            </Button>

          </Stack>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
              <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
              Font Color:
            </Typography>
            <input
              type="color"
              value={fontcolor}
              onChange={(e) => setFontcolor(e.target.value)}
              style={{ width: 28, height: 28, padding: 0, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
            />
          </Box>
          <Tooltip title="Download PDF">
            <IconButton size="small" onClick={handleDownloadPDF}>
              <PictureAsPdfIcon fontSize="small" color="warning" />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1 }} />
          <Button variant="outlined" sx={{ bgcolor: '#3f2d01', color: '#fff' }} startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </Button>

          <Button variant="outlined" sx={{ bgcolor: 'orange', color: '#fff' }} startIcon={<PrintIcon />} onClick={onPrint}>
            Print
          </Button>
        </Stack>
      </Card>

      {/* Cards Grid */}
      {layout ? (
        <Box id="student-id-cards-root">
          <Grid container spacing={2}>
            {students?.map((stu) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={stu.id}>
                <Card
                  elevation={3}
                  sx={{
                    height: 206,
                    width: 328,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 3px 12px rgba(0,0,0,0.2)",
                  }}
                >

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      // bgcolor: "#fff",
                      bgcolor: colors[stu.id] || theme.palette.primary.main,
                      borderBottom: "1px solid #ddd",
                      p: 0.4,
                      gap: 1.5,
                    }}
                  >
                    <Avatar
                      src={stu.logo}
                      alt="School Logo"
                      sx={{ width: 60, height: 60, borderRadius: 1 }}
                    />
                    <Box sx={{ color: fontcolor, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 12 }}>
                        {stu.school_name}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", lineHeight: 1.2, fontSize: 10 }}>
                        {stu.school_address} | Tel: {stu.school_phone}
                      </Typography>
                      {/* <Typography variant="caption">Tel: {stu.school_phone}</Typography> */}
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        // height: 72,
                        width: "100%",
                        display: "flex",
                        alignItems: "flex-start",
                        p: 0.5,
                        gap: 0.8,
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: 'column', alignItems: "center" }}>
                        <Avatar
                          src={stu.photo || undefined}
                          sx={{ width: 70, height: 70, borderRadius: 1 }}
                        >
                          {(!stu.photo && stu.name) ? stu.name.split(" ").map(n => n[0]).slice(0, 2).join("") : ""}
                        </Avatar>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {"Principal"}
                        </Typography>
                      </Box>
                      <Box sx={{
                        display: 'grid',
                        color: "#1f0066ff",
                        rowGap: 0,
                      }}>

                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 14 }}>{stu.name}</Typography>
                        <Typography variant="caption" lineHeight={1.3}><strong>Class:</strong> {stu.standard} | {stu.section} || <strong>  DoB: </strong>
                          {new Date(stu.dob).toLocaleDateString()}</Typography>

                        <Typography variant="caption" lineHeight={1.3} ><strong>Father:</strong> {stu.father}</Typography>

                        {/* <Typography variant="caption"><strong> DoB: </strong>
                          {new Date(stu.dob).toLocaleDateString()}
                        </Typography> */}
                        <Typography variant="caption" lineHeight={1.3}><strong>Mobile:</strong> {stu.mobile}</Typography>

                        <Typography variant="caption" lineHeight={1.1} maxWidth={'100%'} noWrap >
                          <strong>Add.:</strong> {stu.address}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      backgroundColor: colors[stu.id] || theme.palette.primary.main,
                      padding: "6px 10px",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: "bold", color: fontcolor }}>
                      IDENTITY CARD
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: "bold", color: fontcolor }}>
                      Academic Session: {stu.academicYear}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box id="student-id-cards-root">
          <Grid container spacing={0}>
            {students?.map((student) => (
              <div style={{ display: "flex", justifyContent: "center", margin: "10px" }}>
                <Card
                  sx={{
                    width: 205,
                    height: 328,
                    border: "3px solid " + `${colors[student.id] || theme.palette.primary.main}`,
                    borderRadius: "10px",
                    boxShadow: 5,
                    background: "linear-gradient(135deg, #e3f2fd, #ffffff)",
                    position: "relative",
                    display: "flex",          // make card flex
                    flexDirection: "column",  // vertical layout
                  }}
                >
                  <div
                    style={{
                      background: colors[student.id] || theme.palette.primary.main,
                      color: fontcolor,
                      textAlign: "center",
                      display: "flex",
                      justifyContent: "space-around",
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      src={student.logo}
                      alt="School Logo"
                      sx={{ width: 50, height: 50, borderRadius: 1 }}
                    />
                    <Typography variant="subtitle2" fontWeight="bold" lineHeight={1.1} fontSize={12}>
                      {student.school_name}
                    </Typography>
                  </div>

                  <div
                    style={{
                      background: colors[student.id] || theme.palette.primary.main,
                      color: fontcolor,
                      textAlign: "center",
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "space-around",
                      alignItems: "center",
                      paddingTop: 3,
                      paddingBottom: 3,
                    }}
                  >
                    <Typography variant="caption" lineHeight={1.1} fontSize={10}>
                      {student.school_address} | Tel: {student.school_phone}
                    </Typography>
                  </div>

                  <CardContent sx={{ textAlign: "center", py: 0.5, flexGrow: 1 }}>
                    <Avatar
                      src={student.photo}
                      alt={student.name}
                      sx={{
                        width: 80,
                        height: 80,
                        margin: "0px auto",
                        border: "2px solid" + `${colors[student.id] || theme.palette.primary.main}`,
                      }}
                    />
                    <Typography variant="subtitle2" color="blue" fontWeight="bold">
                      {student.name}
                    </Typography>

                    <Box
                      sx={{
                        color: "black",
                        display: "grid",
                        justifyContent: "center",
                        justifyItems: "start",
                        rowGap: 0,
                        gap: '2px 0',
                        width: '100%',
                      }}
                    >
                      <InfoRow icon={FamilyRestroom} label="Father" value={student.father} color={colors[student.id]} noWrap />
                      <InfoRow icon={School} label="Class" value={`${student.standard} | ${student.section}`} color={colors[student.id]} />
                      <InfoRow icon={CalendarToday} label="DoB" value={student.dob} color={colors[student.id]} />
                      <InfoRow icon={Phone} label="Mob" value={student.mobile} color={colors[student.id]} />
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, width: '100%' }}>
                        <Home sx={{ fontSize: 14, color: `${colors[student.id] || theme.palette.primary.main}`, mt: 0.2 }} />
                        <Typography
                          fontSize={11}
                          variant="caption"
                          lineHeight={1.3}
                          maxWidth={'100%'}
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                            cursor: 'pointer',
                          }}
                        >
                          {student.address}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>

                  {/* Fixed Footer */}
                  <div
                    style={{
                      background: colors[student.id] || theme.palette.primary.main,
                      color: fontcolor,
                      textAlign: "center",
                      padding: "5px",
                      borderBottomLeftRadius: "5px",
                      borderBottomRightRadius: "5px",
                    }}
                  >
                    <Typography variant="caption" fontWeight="bold">
                      Academic Session:  {student.academicYear}
                    </Typography>
                  </div>
                </Card>

              </div>
            ))}
          </Grid >
        </Box >
      )}
    </Box >
  );
}